const API_BASE = import.meta.env.VITE_API_BASE_URL;

type RequestOptions = {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
};

async function request<T>(
  path: string,
  token: string,
  opts: RequestOptions = {}
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: opts.method || "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...opts.headers,
    },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(err.message || `API Error ${res.status}`);
  }

  return res.json();
}

// --- Projects ---

export function getProjects(token: string) {
  return request<import("@/types").Project[]>("/project", token);
}

export function getProjectDetail(token: string, projectId: string) {
  return request<{ data: import("@/types").Project }>(
    `/project/detail/${projectId}`,
    token
  );
}

// --- Command Center / Briefing ---

export function getBriefing(token: string) {
  return request<import("@/types").BriefingData>("/command-center/briefing", token);
}

// --- Email Generation ---

export function generateEmail(
  token: string,
  payload: import("@/types").GenerateEmailRequest
) {
  return request<{ content: string }>("/assistant/generate-email", token, {
    method: "POST",
    body: payload,
  });
}

// --- Companies ---

export function getUserCompanies(token: string) {
  return request<{ data: import("@/types").Company[] }>(
    "/auth/companies/my",
    token
  );
}

// --- Assistant Threads ---

export function getThreads(token: string, projectId?: string) {
  const query = projectId ? `?projectId=${projectId}` : "";
  return request<import("@/types").ThreadsGroupedByProject[]>(
    `/assistant/threads${query}`,
    token
  );
}

export function getThreadHistory(
  token: string,
  threadId: string,
  limit = 50,
  offset = 0
) {
  return request<{
    messages: import("@/types").ThreadHistoryMessage[];
    total: number;
  }>(`/assistant/threads/${threadId}/history?limit=${limit}&offset=${offset}`, token);
}
